import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

import { createMap } from 'maplibre-gl-js-amplify';
import {
  NavigationControl,
  Map,
  Marker,
  LngLatBounds,
  Popup,
  MarkerOptions,
} from 'maplibre-gl';
import numeral from 'numeral';
import { format } from 'date-fns';
import { ShipmentSensorAlertModel } from '../shipment-sensor-alert-detail/shipment-sensor-alert-detail.component';

/** センサの計測結果のViewModel */
export type ShipmentSensingLocationViewModel = {
  shipmentName?: string;
  shipmentId?: string;
  deviceId?: string;
  deviceName?: string;
  sensor?: string;
  status?: string;
  threshold?: number;
  value?: number;
  eventAt?: string;
  location: LocationViewModel;
};

/** 位置情報のViewModel */
export type LocationViewModel = {
  longitude: number;
  latitude: number;
  eventAt: string;
  address?: string;
};

/** センサーのオプション */
export type SensorOption = {
  label: string;
  unit: string;
};

@Component({
  selector: 'app-shipment-alert-map',
  standalone: true,
  imports: [],
  templateUrl: './shipment-alert-map.component.html',
  styleUrl: './shipment-alert-map.component.scss',
})
export class ShipmentAlertMapComponent implements OnChanges {
  /** アラート履歴 */
  @Input({ required: true }) alertList: ShipmentSensorAlertModel[] = [];

  /** 選択されたアラート詳細 */
  @Input() selectedAlertDetail: ShipmentSensorAlertModel | undefined;

  /** 選択されたマーカー情報 */
  @Input() selectedMarker: ShipmentSensorAlertModel | undefined;

  /** タブ切り替えを検知 */
  @Input() displayPageChanged: string | undefined;

  /** マップの移動が開始した際に通知 */
  @Output() mapMoveStart = new EventEmitter<void>();

  /** マップの移動が終了した際に通知 */
  @Output() mapMoveEnd = new EventEmitter<LngLatBounds>();

  /** 選択マーカーを変更した際に通知 */
  @Output() markerChanged = new EventEmitter<ShipmentSensorAlertModel>();

  private map!: Map;
  private markers: Marker[] = [];

  /** マーカー選択時に表示されているポップアップ */
  private activePopup: Popup | null = null;

  /** ホバー時に表示されているポップアップ */
  private hoverPopup: Popup | null = null;

  private readonly sensorOptions: { [key: string]: SensorOption } = {
    temperature: {
      label: '温度',
      unit: '℃',
    },
    humidity: {
      label: '湿度',
      unit: '%',
    },
    pressure: {
      label: '気圧',
      unit: 'hPa',
    },
    light: {
      label: '照度',
      unit: 'lx',
    },
    tilt: {
      label: '傾斜',
      unit: '°',
    },
  };

  private readonly markerColors: { [key: string]: string } = {
    alertmin: '#EF4444',
    alertmax: '#EF4444',
    alert: '#EF4444',
    prealertmin: '#FFD700',
    prealertmax: '#FFD700',
    prealert: '#FFD700',
  };

  private isAdded: { flag: boolean; index: number } | undefined;

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (
      changes['displayPageChanged'] !== undefined &&
      changes['displayPageChanged'].currentValue === 'shipment'
    ) {
      await this.createMapContainer();
    }
    if (changes['alertList'] !== undefined) {
      const alertList: ShipmentSensorAlertModel[] =
        changes['alertList'].currentValue;
      this.activePopup?.remove();
      this.initMapLayers(alertList).then();
    }
    if (changes['selectedAlertDetail'] !== undefined) {
      const selectedAlertDetail = changes['selectedAlertDetail'].currentValue;
      this.initMapLayers(this.alertList, selectedAlertDetail, false).then();
    }
    if (changes['selectedMarker'] !== undefined) {
      const selectedMarker = changes['selectedMarker'].currentValue;
      this.initMapLayers(this.alertList, selectedMarker, false).then();
    }
  }

  private async createMapContainer() {
    this.map = await createMap({
      container: 'shipment-map',
      center: [139.767, 35.681],
      zoom: 11,
      minZoom: 1,
    });

    this.map.addControl(
      new NavigationControl({
        visualizePitch: true,
      }),
    );

    this.map.on('movestart', () => {
      this.mapMoveStart.emit();
    });

    this.map.on('moveend', () => {
      const bounds = this.map.getBounds();
      this.mapMoveEnd.emit(bounds);
    });

    this.activePopup?.remove();
  }

  private async waitMapLoaded(): Promise<boolean> {
    if (this.map === undefined) {
      return false;
    } else if (this.map.loaded()) {
      return true;
    }

    return new Promise<boolean>((resolve) => {
      this.map.on('load', () => {
        resolve(true);
      });
    });
  }

  private async initMapLayers(
    alertList: ShipmentSensorAlertModel[],
    selectedInfo?: ShipmentSensorAlertModel,
    pan = true,
  ) {
    // マップが表示するまで待機
    if (!(await this.waitMapLoaded())) {
      return;
    }

    // マーカー表示
    if (selectedInfo) {
      this.setMarkers(alertList, selectedInfo);
    } else {
      this.setMarkers(alertList, this.selectedMarker);
    }

    // PANする
    if (pan) {
      this.panToMarkers(this.markers);
    }
  }

  /**
   * アラートリストからマーカーを設定する
   * @param alertList アラートリスト
   * @param selectedMarker 選択されたアラートの詳細情報
   */
  private setMarkers(
    alertList: ShipmentSensorAlertModel[],
    selectedMarker?: ShipmentSensorAlertModel,
  ) {
    // 初期化
    this.markers.forEach((marker) => marker.remove());
    this.markers = [];

    if (this.isAdded?.flag) {
      const item = alertList.pop();
      alertList.splice(this.isAdded.index, 0, item!);
    }

    if (selectedMarker) {
      const index = alertList.findIndex(
        (value) =>
          value.location.latitude === selectedMarker.location.latitude &&
          value.location.longitude === selectedMarker.location.longitude &&
          value.value === selectedMarker.value &&
          value.deviceId === selectedMarker.deviceId &&
          value.status === selectedMarker.status &&
          value.eventAt === selectedMarker.eventAt,
      );
      const [item] = alertList.splice(index, 1);
      alertList.push(item);

      this.isAdded = {
        flag: true,
        index: index,
      };
    }

    // マーカー追加
    for (const sensingValue of alertList) {
      const longitude = sensingValue.location?.longitude;
      const latitude = sensingValue.location?.latitude;
      // 位置情報がない場合（null or undefined）の場合はマーカーを追加しない
      if (longitude == null || latitude == null) {
        continue;
      }
      const sensorStatus = sensingValue.status?.toLowerCase() || '';
      const markerColor = this.markerColors[sensorStatus];

      // 選択済みアラートがある場合はマーカーを大きくする
      const markerOptions: MarkerOptions =
        selectedMarker?.location?.latitude === latitude &&
        selectedMarker?.location?.longitude === longitude
          ? { color: markerColor, scale: 1.5 }
          : { color: markerColor };

      const marker = new Marker(markerOptions)
        .setLngLat({ lon: longitude, lat: latitude })
        .addTo(this.map);

      // マーカークリック時の挙動設定
      marker
        .getElement()
        .addEventListener('click', () =>
          this.handleMarkerClick(sensingValue, latitude, longitude),
        );

      // マーカーホバー時の挙動設定
      marker
        .getElement()
        .addEventListener('mouseenter', () =>
          this.handleMarkerMouseEnter(
            marker,
            sensingValue,
            latitude,
            longitude,
          ),
        );

      this.markers.push(marker);
    }

    if (selectedMarker) {
      const longitude = selectedMarker.location?.longitude;
      const latitude = selectedMarker.location?.latitude;

      if (longitude !== undefined && latitude !== undefined) {
        this.showPopupForAlert(selectedMarker, longitude, latitude);
      }
    } else {
      this.activePopup?.remove();
    }
  }

  /**
   * マーカークリック時のポップアップ表示状態を制御する
   * @param sensingValue センサの計測結果
   * @param longitude 経度
   * @param latitude 緯度
   */
  private handleMarkerClick(
    sensingValue: ShipmentSensorAlertModel,
    latitude: number,
    longitude: number,
  ): void {
    if (
      this.selectedMarker &&
      this.selectedMarker.location?.latitude === latitude &&
      this.selectedMarker.location?.longitude === longitude
    ) {
      // 選択状態のマーカーがクリックされた場合
      this.markerChanged.emit(undefined); // 選択状態を解除
      this.activePopup?.remove(); // ポップアップを削除
      if (this.hoverPopup) {
        this.hoverPopup.remove();
      }
    } else {
      // 非選択状態のマーカーがクリックされた場合
      // 一度選択状態をすべて解除する
      if (this.activePopup) {
        this.activePopup.remove();
      }
      if (this.selectedAlertDetail) {
        this.markerChanged.emit(undefined);
        this.activePopup?.remove();
      }

      // 新しいアラートを選択状態にする
      this.markerChanged.emit(sensingValue);

      this.showPopupForAlert(sensingValue, longitude, latitude);

      if (this.hoverPopup) {
        this.hoverPopup.remove();
      }
    }
  }

  /**
   * マーカーホバー時のポップアップ表示状態を制御する
   * @param marker マーカー
   * @param sensingValue センサの計測結果
   * @param longitude 経度
   * @param latitude 緯度
   */
  private handleMarkerMouseEnter(
    marker: Marker,
    sensingValue: ShipmentSensorAlertModel,
    latitude: number,
    longitude: number,
  ): void {
    marker.getElement().style.cursor = 'pointer';

    if (
      !this.selectedMarker ||
      (this.selectedMarker &&
        this.selectedMarker.location?.latitude !== latitude &&
        this.selectedMarker.location?.longitude !== longitude)
    ) {
      const description = this.createPointDescription(sensingValue);
      this.hoverPopup = new Popup({
        closeButton: false,
        closeOnClick: false,
      })
        .setLngLat({ lon: longitude, lat: latitude })
        .setHTML(description)
        .setOffset([0, -20])
        .addTo(this.map);

      marker.getElement().addEventListener('mouseleave', () => {
        marker.getElement().style.cursor = '';
        if (this.hoverPopup) {
          this.hoverPopup.remove();
        }
      });
    }
  }

  /**
   * 指定されたアラートの内容のポップアップを指定の座標に表示する
   * @param sensingValue センサの計測結果
   * @param longitude 経度
   * @param latitude 緯度
   */
  private showPopupForAlert(
    sensingValue: ShipmentSensorAlertModel,
    longitude: number,
    latitude: number,
  ): void {
    if (this.activePopup) {
      this.activePopup.remove();
    }

    const description = this.createPointDescription(sensingValue);
    this.activePopup = new Popup({
      closeButton: false,
      closeOnClick: false,
    })
      .setLngLat({ lon: longitude, lat: latitude })
      .setHTML(description)
      .setOffset([0, -20])
      .addTo(this.map);
  }

  /**
   * マーカーのツールチップポップアップのテキストを生成する
   * @param deviceName
   * @param sensingType
   * @param value
   * @param eventAt
   * @returns
   */
  private createPointDescription(
    sensingValue: ShipmentSensorAlertModel,
  ): string {
    const deviceName =
      sensingValue.deviceName || this.selectedAlertDetail?.deviceName;
    const deviceId =
      sensingValue.deviceId || this.selectedAlertDetail?.deviceId;

    const shipmentName = sensingValue.shipmentName;
    const shipmentId = sensingValue.shipmentId;

    const sensorOption = this.sensorOptions[sensingValue.sensor.label];
    const status = this.getAlertStatus(sensingValue.status);

    let sensorDescription = '';
    if (sensorOption) {
      const label = sensorOption.label;
      const unit = sensorOption.unit;

      const thresholdValue =
        sensingValue.threshold == null
          ? numeral(sensingValue.threshold).format('0.0')
          : '-';

      const threshold = `(${status.thresholdType}${thresholdValue}&nbsp;${unit})`;

      if (sensingValue.value == null) {
        sensorDescription = `${label}：-`;
      } else {
        const formattedValue = numeral(sensingValue.value).format('0.0');
        sensorDescription = `${label}${status.alertStatus}：${formattedValue}&nbsp;${unit}${threshold}`;
      }
    }

    const eventAtTimestamp =
      sensingValue.eventAt == null
        ? '-'
        : format(new Date(sensingValue.eventAt), 'yyyy/MM/dd HH:mm:ss');

    const description = `
        <b>デバイス名：${deviceName || deviceId}</b>
        <div><b>配送名：${shipmentName || shipmentId}</b></div>
        <div>アラート日時：${eventAtTimestamp}</div>
        <div>${sensorDescription}</div>
        <div>住所：${sensingValue.location?.address ?? ''}</div>
    `;

    return description;
  }

  private panToMarkers(markers: Marker[]) {
    if (markers.length < 1) {
      this.map.setCenter([139.767, 35.681]);
      this.map.setZoom(11);
      return;
    }

    // マーカーの座標を取得
    const markerCoordinates = markers.map((marker) => marker.getLngLat());

    // マーカーの座標を包括する範囲を計算
    const bounds = new LngLatBounds();
    markerCoordinates.forEach((coord) => bounds.extend(coord));

    // マップをPANしてマーカーが見えるようにする
    this.map.fitBounds(bounds, { padding: 50, maxZoom: 16 });
  }

  private getAlertStatus(status: string): {
    alertStatus: string;
    thresholdType: string;
  } {
    switch (status) {
      case 'AlertMax':
        return { alertStatus: '上限アラート', thresholdType: '上限' };
      case 'AlertMin':
        return { alertStatus: '下限アラート', thresholdType: '下限' };
      case 'PreAlertMax':
        return { alertStatus: '上限ワーニング', thresholdType: '上限' };
      case 'PreAlertMin':
        return { alertStatus: '下限ワーニング', thresholdType: '下限' };
      case 'Alert':
        return { alertStatus: 'アラート', thresholdType: '' };
      case 'PreAlert':
        return { alertStatus: 'ワーニング', thresholdType: '' };
      default:
        return { alertStatus: '', thresholdType: '' };
    }
  }
}
